import React from 'react';
import { Box, Button, Stack, Theme, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { getNavigationTreeLineColor } from 'components/utils';
import { surfaceBorderSx, surfaceBgSx } from 'components/common/surfaces';

export const stackStyle = (theme: Theme, topSpace = '0px') => ({
  alignItems: 'flex-start',
  padding: '3px',
  paddingTop: '0px',
  minWidth: '172px',
  ...surfaceBgSx,
  ...surfaceBorderSx,
  alignSelf: 'flex-start',
  position: 'sticky',
  top: topSpace,

  [theme.breakpoints.down('md')]: {
    position: 'relative',
    mb: 2,
    minWidth: 0,
    top: 0,
    width: '100%',
  },
});

export const stackContentStyle = (theme: Theme) => ({
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  alignSelf: 'stretch',
  pl: 2,
  display: 'flex',
  flexDirection: 'column' as const,
  overflow: 'auto',
  pt: '3px',
  pr: '3px',
  mt: '-3px',
  mr: '-3px',

  [theme.breakpoints.down('md')]: {
    pl: 0,
  },
});

export const stackContainerStyle = (theme: Theme) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  position: 'relative',
  flex: 1,
  minHeight: 0,
  width: '100%',

  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
});

const narrowNavScrollSx = {
  display: 'flex',
  flexDirection: 'row' as const,
  flexWrap: 'nowrap' as const,
  alignItems: 'center',
  gap: 0.5,
  overflowX: 'auto' as const,
  overflowY: 'hidden' as const,
  WebkitOverflowScrolling: 'touch',
  scrollbarWidth: 'thin' as const,
  overscrollBehaviorX: 'contain' as const,
  maxWidth: '100%',
};

/** Primary stack row — matches drawer items (AuthLayout): secondary bar + contrast text when selected */
export const stackButtonStyle = (theme: Theme, tab: number, tabValue: number) => {
  const selected = tab === tabValue;
  return {
    justifyContent: 'flex-start' as const,
    textAlign: 'left' as const,
    width: '100%',
    padding: '5px 10px',
    fontSize: '13px',
    backgroundColor: selected ? theme.palette.primary.main : 'transparent',
    color: selected ? theme.palette.primary.contrastText : theme.palette.text.primary,
    borderRadius: '6px',
    marginTop: '2px !important',
    transition: 'background-color 0.2s ease',
    [theme.breakpoints.down('md')]: {
      minWidth: 0,
    },
    '&:hover': {
      backgroundColor: selected ? theme.palette.primary.main : theme.palette.action.hover,
    },
  };
};

/** Sub-rows — same as AuthLayout submenu ListItem: secondary on hover/active, contrast text on tinted bg */
export const stackSubButtonStyle = (theme: Theme, isParentSelected: boolean, sub: number, subTabValue?: number) => {
  const selected = isParentSelected && typeof subTabValue === 'number' && sub === subTabValue;
  return {
    justifyContent: 'flex-start' as const,
    textAlign: 'left' as const,
    width: '100%',
    padding: '4px 8px',
    minHeight: 26,
    backgroundColor: selected ? theme.palette.primary.main : 'transparent',
    color: selected ? theme.palette.primary.contrastText : alpha(theme.palette.text.primary, 0.8),
    borderRadius: '4px',
    fontSize: '12px',
    lineHeight: 1.3,
    textTransform: 'none' as const,
    transition: 'background-color 0.2s ease',
    [theme.breakpoints.down('md')]: {
      minWidth: 0,
    },
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
  };
};

type StackTabItem = {
  label: string | React.ReactNode;
  value: number;
  testId?: string;
  subTabs?: { label: string; value: number; testId?: string }[];
};

type StackSubTabItem = NonNullable<StackTabItem['subTabs']>[number];

type FacebookSubTabsProps = {
  theme: Theme;
  parentTab: StackTabItem;
  subTabs: StackSubTabItem[];
  isParentSelected: boolean;
  subTabValue?: number;
  setTabValue: (value: number) => void;
  setSubTabValue: (value: number, parentTabValue?: number) => void;
};

const renderSubTabsVertical = ({
  theme,
  parentTab,
  subTabs,
  isParentSelected,
  subTabValue,
  setTabValue,
  setSubTabValue,
}: FacebookSubTabsProps) => (
  <Box
    sx={t => ({
      pl: `calc(${t.spacing(1.5)} + 5px)`,
      pr: t.spacing(1),
      mt: 0.75,
      mb: 0,
      pb: 0,
      width: '100%',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
      overflowX: 'hidden',
      overflowY: 'visible',
    })}
  >
    {subTabs.map((sub, idx) => {
      const isLast = idx === subTabs.length - 1;
      return (
        <Box key={sub.value} sx={{ position: 'relative', pl: 3 }}>
          {!isLast && (
            <Box
              sx={t => ({
                position: 'absolute',
                left: 8,
                top: 0,
                bottom: '-4px',
                width: 0,
                borderLeft: `1px solid ${getNavigationTreeLineColor(t)}`,
                pointerEvents: 'none',
              })}
            />
          )}
          <Box
            sx={t => ({
              position: 'absolute',
              left: 8,
              top: 0,
              width: 16,
              height: '50%',
              borderLeft: `1px solid ${getNavigationTreeLineColor(t)}`,
              borderBottom: `1px solid ${getNavigationTreeLineColor(t)}`,
              borderBottomLeftRadius: '12px',
              pointerEvents: 'none',
            })}
          />
          <Button
            color="secondary"
            variant="text"
            data-testid={sub.testId}
            onClick={() => {
              setTabValue(parentTab.value);
              setSubTabValue(sub.value, parentTab.value);
            }}
            sx={stackSubButtonStyle(theme, isParentSelected, sub.value, subTabValue)}
          >
            {sub.label}
          </Button>
        </Box>
      );
    })}
  </Box>
);

const renderSubTabsHorizontal = ({
  theme,
  parentTab,
  subTabs,
  isParentSelected,
  subTabValue,
  setTabValue,
  setSubTabValue,
}: FacebookSubTabsProps) => (
  <Box
    sx={{
      position: 'relative',
      mt: 0.75,
      width: '100%',
      minWidth: 0,
      boxSizing: 'border-box',
      pl: 1,
      pr: 0.5,
    }}
  >
    <Box
      sx={t => ({
        position: 'absolute',
        left: 8,
        top: 0,
        width: 16,
        height: 14,
        borderLeft: `1px solid ${getNavigationTreeLineColor(t)}`,
        borderBottom: `1px solid ${getNavigationTreeLineColor(t)}`,
        borderBottomLeftRadius: '12px',
        pointerEvents: 'none',
        zIndex: 1,
      })}
    />
    <Box
      sx={{
        ...narrowNavScrollSx,
        pl: 3.5,
        gap: 0.5,
        alignItems: 'center',
      }}
    >
      {subTabs.map(sub => (
        <Button
          color="secondary"
          key={sub.value}
          variant="text"
          data-testid={sub.testId}
          onClick={() => {
            setTabValue(parentTab.value);
            setSubTabValue(sub.value, parentTab.value);
          }}
          sx={{
            ...stackSubButtonStyle(theme, isParentSelected, sub.value, subTabValue),
            width: 'auto',
            minWidth: 0,
            flexShrink: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {sub.label}
        </Button>
      ))}
    </Box>
  </Box>
);

const StackTabs = ({
  tabs,
  tabValue,
  setTabValue,
  renderTabContent,
  noSpacing: _noSpacing = false,
  contentKey,
  topSpace,
  subTabValue,
  setSubTabValue,
}: {
  contentKey?: string;
  tabs: StackTabItem[];
  tabValue: number;
  setTabValue: (value: number) => void;
  renderTabContent: () => React.ReactNode;
  noSpacing?: boolean;
  topSpace?: string;
  subTabValue?: number;
  setSubTabValue?: (value: number, parentTabValue?: number) => void;
}) => {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
  const selectedTab = tabs.find(tab => tab.value === tabValue);

  const selectTab = (tab: StackTabItem) => {
    setTabValue(tab.value);

    if (tab.subTabs && tab.subTabs.length > 0 && setSubTabValue) {
      setSubTabValue(tab.subTabs[0].value, tab.value);
    }
  };

  if (isNarrow) {
    return (
      <Box sx={stackContainerStyle}>
        <Box
          sx={{
            width: '100%',
            mb: 2,
            p: 0.5,
            minWidth: 0,
            ...surfaceBgSx,
            ...surfaceBorderSx,
          }}
        >
          <Box sx={narrowNavScrollSx}>
            {tabs.map(tab => (
              <Button
                color="secondary"
                key={tab.value}
                variant="text"
                data-testid={tab.testId}
                onClick={() => selectTab(tab)}
              sx={{
                ...stackButtonStyle(theme, tab.value, tabValue),
                width: 'auto',
                minWidth: 0,
                flexShrink: 0,
                whiteSpace: 'nowrap',
                marginTop: '0 !important',
              }}
              >
                {tab.label}
              </Button>
            ))}
          </Box>

          {selectedTab?.subTabs && selectedTab.subTabs.length > 0 && setSubTabValue
            ? renderSubTabsHorizontal({
                theme,
                parentTab: selectedTab,
                subTabs: selectedTab.subTabs,
                isParentSelected: true,
                subTabValue,
                setTabValue,
                setSubTabValue,
              })
            : null}
        </Box>

        <Box key={contentKey} sx={stackContentStyle}>
          {renderTabContent()}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={stackContainerStyle}>
      <Stack spacing={0.5} sx={stackStyle(theme, topSpace ?? '0px')}>
        {tabs.map(tab => (
          <React.Fragment key={tab.value}>
            <Button
              color="secondary"
              variant="text"
              data-testid={tab.testId}
              onClick={() => selectTab(tab)}
              sx={{
                ...stackButtonStyle(theme, tab.value, tabValue),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              {tab.label}
            </Button>

            {tab.subTabs && tab.subTabs.length > 0 && setSubTabValue
              ? renderSubTabsVertical({
                  theme,
                  parentTab: tab,
                  subTabs: tab.subTabs,
                  isParentSelected: tab.value === tabValue,
                  subTabValue,
                  setTabValue,
                  setSubTabValue,
                })
              : null}
          </React.Fragment>
        ))}
      </Stack>

      <Box key={contentKey} sx={stackContentStyle}>
        {renderTabContent()}
      </Box>
    </Box>
  );
};

export default StackTabs;
